import React, { useCallback, useMemo, useRef, useState } from 'react'
import Dropzone, { FileRejection, DropzoneRef } from 'react-dropzone'

import { uploadFile } from '@harvey-frontend/api'
import { FileType } from '@harvey-frontend/types/file'
import { onDrop } from '@harvey-frontend/utils/dropzone'
import { mbToBytes } from '@harvey-frontend/utils/file-utils'
import { cn } from '@harvey-frontend/utils/utils'
import * as Sentry from '@sentry/react'
import { CircleAlert, CloudUpload, Trash } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'

import { Dropzone as HarveyDropzone } from '@harvey-frontend/components/common/dropzone/dropzone'
import DropzoneDescription from '@harvey-frontend/components/common/dropzone/dropzone-description'
import { Button } from '@harvey-frontend/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
} from '@harvey-frontend/components/ui/card'
import { Checkbox } from '@harvey-frontend/components/ui/checkbox'
import { Icon } from '@harvey-frontend/components/ui/icon/icon'
import { ScrollArea } from '@harvey-frontend/components/ui/scroll-area'
import { Spinner } from '@harvey-frontend/components/ui/spinner'
import { processFiles } from 'components/assistant/utils/assistant-api'

import { FileState, FileStatus } from '../stores/app-store'
import useAppStore from '../stores/store'

const ACCEPTED_FILE_TYPES = [FileType.WORD, FileType.WORD_LEGACY, FileType.PDF]
const MAX_FILE_COUNT = 5
const MAX_FILE_SIZE_IN_MB = 20

const FileItem = ({
  isDisabled,
  isIconVisible,
  fileState,
}: {
  isDisabled: boolean
  isIconVisible?: boolean
  fileState: FileState
}) => {
  const [markFileSelected, removeFile] = useAppStore(
    useShallow((s) => [s.markFileSelected, s.removeFile])
  )

  const deleteHandler = () => {
    fileState.abortController.abort()
    removeFile(fileState.name)
  }

  const onCheckedChange = () => {
    markFileSelected(fileState.name, !fileState.selected)
  }

  return (
    <div className="group flex h-8 w-full shrink-0 items-center justify-between p-2 pr-0">
      <div className="flex items-center">
        {isIconVisible && (
          <div className="w-5">
            {fileState?.status === FileStatus.UPLOADING && (
              <Spinner size="xxs" />
            )}
            {fileState?.status === FileStatus.UPLOADED && (
              <Checkbox
                className="h-3 w-3"
                disabled={isDisabled}
                checked={fileState.selected}
                onCheckedChange={onCheckedChange}
              />
            )}
            {fileState?.status === FileStatus.FAILED && (
              <Icon icon={CircleAlert} className="size-3 text-destructive" />
            )}
          </div>
        )}
        <p
          className={cn('truncate pl-4 text-xs', {
            'text-muted': isDisabled || !fileState.selected,
          })}
        >
          <span className="text-sm">{fileState.name}</span>
        </p>
      </div>
      <Button
        data-testid="delete-file-button"
        variant="text"
        size="smIcon"
        className="shrink-0 text-xs text-destructive opacity-0 transition-opacity duration-200 ease-in-out hover:text-destructive group-hover:opacity-100"
        onClick={deleteHandler}
      >
        <Icon icon={Trash} className="h-3 w-3" />
      </Button>
    </div>
  )
}

const UploadFileButton = ({
  numUploadedFiles,
  addMoreHandler,
}: {
  numUploadedFiles: number
  addMoreHandler: () => void
}) => {
  return numUploadedFiles >= MAX_FILE_COUNT ? (
    <div className="p-2 text-xs text-muted">
      You can add up to {MAX_FILE_COUNT} files for Harvey to use as precedent
      for your revisions.
    </div>
  ) : (
    <div className="flex w-full gap-1 p-2">
      <Button
        disabled={numUploadedFiles >= MAX_FILE_COUNT}
        className="flex w-full gap-1 p-2"
        variant="outline"
        onClick={addMoreHandler}
      >
        <Icon icon={CloudUpload} />
        Browse files
      </Button>
    </div>
  )
}

const TooManyFilesContent = () => {
  return (
    <div
      data-testid="too-many-files-warning"
      className="flex h-full flex-col items-center justify-center"
    >
      <Icon icon={CircleAlert} className="size-10 text-destructive" />
      <p className="text-sm text-muted">
        You can add up to {MAX_FILE_COUNT} files.
      </p>
    </div>
  )
}

const FileUpload = ({ isFullHeight }: { isFullHeight?: boolean }) => {
  const [collapsed] = useState(false)
  const [dropzoneError, setDropzoneError] = useState<boolean>(false)
  const {
    addUploadingFiles,
    eventId,
    files,
    isApplyingSuggestion,
    isDropzoneLoading,
    isFetching,
    setFileFailed,
    setFileUploaded,
    setIsDropzoneLoading,
  } = useAppStore(
    useShallow((s) => ({
      addUploadingFiles: s.addUploadingFiles,
      eventId: s.eventId,
      files: s.files,
      isApplyingSuggestion: s.isApplyingSuggestion,
      isDropzoneLoading: s.isDropzoneLoading,
      isFetching: s.isFetching,
      setFileFailed: s.setFileFailed,
      setFileUploaded: s.setFileUploaded,
      setIsDropzoneLoading: s.setIsDropzoneLoading,
    }))
  )

  const dropzoneRef = useRef<DropzoneRef>(null)
  const addMoreHandler = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open()
    }
  }

  const handleAcceptedFiles = useCallback(
    async (files: File[]) => {
      const newFilesToUpload = files.map((file) => {
        const abortController = new AbortController()
        // Fire off a call to upload_file (add to events table)
        // and process_files (upload to LanceDB).
        uploadFile(file, /* throwOnError */ false, {
          signal: abortController.signal,
          eventId: String(eventId),
        })
          .then(async (uploadedFile) => {
            await processFiles(String(eventId), [uploadedFile.id])
            return uploadedFile
          })
          .then(setFileUploaded)
          .catch((error) => {
            Sentry.captureException(error)
            setFileFailed(file.name)
          })
        return {
          name: file.name,
          uploadedFile: null,
          status: FileStatus.UPLOADING,
          selected: false,
          abortController,
        }
      })
      setIsDropzoneLoading(false)
      addUploadingFiles(newFilesToUpload)
    },
    [
      addUploadingFiles,
      setIsDropzoneLoading,
      setFileUploaded,
      setFileFailed,
      eventId,
    ]
  )

  const onDropHandler = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // Semi-hack to get around the fact that we can't control the error toast from the dropzone util.
      // It doesn't fit within the default Word task pane, so override the error here by replacing the
      // dropzone with a custom error message.
      const existingFilesSet = new Set(files.map((f) => f.name))
      const newFiles = acceptedFiles.filter(
        (file) => !existingFilesSet.has(file.name)
      )
      if (existingFilesSet.size + newFiles.length > MAX_FILE_COUNT) {
        setDropzoneError(true)
        setTimeout(() => {
          setDropzoneError(false)
        }, 5000)
        return new Promise(() => {})
      }
      setIsDropzoneLoading(true)
      return onDrop({
        acceptedFiles: newFiles,
        fileRejections,
        acceptedFileTypes: ACCEPTED_FILE_TYPES,
        currentFileCount: files.length,
        maxFiles: MAX_FILE_COUNT,
        maxFileSize: mbToBytes(MAX_FILE_SIZE_IN_MB),
        handleAcceptedFiles,
        handleRejectedFiles: () => {
          setIsDropzoneLoading(false)
        },
      })
    },
    [files, handleAcceptedFiles, setIsDropzoneLoading]
  )

  const isDisabled = isFetching || isApplyingSuggestion || files.length === 0

  const cardBody = useMemo(() => {
    if (dropzoneError) {
      return <TooManyFilesContent />
    }
    const fileItems = (
      <ScrollArea isFullHeight className="h-full" data-testid="file-items">
        {files.map((file) => {
          return (
            <FileItem
              isDisabled={isDisabled}
              isIconVisible={!isFullHeight}
              key={file.name}
              fileState={file}
            />
          )
        })}
      </ScrollArea>
    )

    return (
      <Dropzone ref={dropzoneRef} onDrop={onDropHandler}>
        {({ getRootProps, getInputProps }) => {
          return (
            <>
              <HarveyDropzone
                className={cn(`h-full p-2`, {
                  hidden: collapsed || files.length > 0,
                })}
                descriptionClassName="text-xs"
                applySpacing={false}
                isLoading={isDropzoneLoading}
                dropzone={{ getRootProps, getInputProps }}
                description={
                  <DropzoneDescription
                    fileTypes={ACCEPTED_FILE_TYPES}
                    maxSize={`${MAX_FILE_SIZE_IN_MB}MB`}
                  >
                    <Button
                      data-testid="dropzone-description-button"
                      size="sm"
                      variant="outline"
                      className="mt-2"
                    >
                      Browse files
                    </Button>
                  </DropzoneDescription>
                }
              />
              {!collapsed && files.length > 0 && fileItems}
            </>
          )
        }}
      </Dropzone>
    )
  }, [
    dropzoneError,
    isDisabled,
    files,
    onDropHandler,
    isFullHeight,
    collapsed,
    isDropzoneLoading,
  ])
  return (
    <Card
      className={cn('mt-auto flex w-full flex-col rounded-sm border-0', {
        'flex-1': !collapsed,
        border: files.length > 0,
      })}
    >
      <CardContent
        className={cn('flex h-full flex-col p-0', {
          'p-0': files.length > 0 || collapsed,
        })}
      >
        {cardBody}
      </CardContent>
      {(files.length > 0 || collapsed) && (
        <CardFooter
          className={cn('flex basis-12 flex-row items-center p-0', {
            'border-t': files.length === MAX_FILE_COUNT,
          })}
        >
          <UploadFileButton
            numUploadedFiles={files.length}
            addMoreHandler={addMoreHandler}
          />
        </CardFooter>
      )}
    </Card>
  )
}

export default FileUpload
